import React, { useState, useEffect } from 'react';
import {
  Layout, Card, Row, Col, Table, Button, Select, Input,
  Tag, Space, Tooltip, message, Divider,
  DatePicker
} from 'antd';
import {
  DownloadOutlined, ReloadOutlined, SearchOutlined,
  LeftOutlined, RightOutlined, TeamOutlined,
  ClockCircleOutlined, ArrowLeftOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import api from '../api';
import Sidebar from './Sidebar';
import MainHeader from './MainHeader';

dayjs.extend(isoWeek);

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;

export default function LabourWagesReport({ isEmbedded = false }) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Date Range (Default current week Monday to Saturday)
  const getCurrentMonSat = () => {
    const now = dayjs();
    const day = now.day(); // 0 is Sun, 1 is Mon...
    const diffToMon = day === 0 ? -6 : 1 - day;
    const mon = now.add(diffToMon, 'day').startOf('day');
    const sat = mon.add(5, 'day').endOf('day');
    return [mon, sat];
  };

  const [dateRange, setDateRange] = useState(getCurrentMonSat());
  const [department, setDepartment] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentsList, setDepartmentsList] = useState([]);

  // Report Data
  const [reportData, setReportData] = useState([]);
  const [weekDays, setWeekDays] = useState([]);

  // Fetch departments list
  useEffect(() => {
    const fetchDepts = async () => {
      try {
        const res = await api.get('/admin/departments').catch(() => null);
        if (res?.data?.success && Array.isArray(res.data.departments || res.data.data)) {
          const list = res.data.departments || res.data.data;
          const items = list
            .map((d) => (typeof d === 'string' ? d : d.name || d.department || d.value))
            .filter(Boolean);
          if (items.length > 0) {
            setDepartmentsList(Array.from(new Set(items)));
            return;
          }
        }

        const bfRes = await api.get('/admin/business-functions').catch(() => null);
        if (bfRes?.data?.success) {
          const list = bfRes.data.data || [];
          const deptFn = list.find((f) => String(f.name || '').toLowerCase() === 'department');
          const values = Array.isArray(deptFn?.values) ? deptFn.values : [];
          const items = values
            .filter((v) => v && v.value)
            .map((v) => String(v.value).trim())
            .filter(Boolean);
          setDepartmentsList(Array.from(new Set(items)));
        }
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };
    fetchDepts();
  }, []);

  // Fetch Report Data
  const fetchReport = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;
    setLoading(true);
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');

      const params = {
        startDate,
        endDate,
      };
      if (department && department !== 'all') params.department = department;
      if (searchQuery) params.search = searchQuery;

      const res = await api.get('/admin/reports/labour-wages', { params });
      if (res.data?.success) {
        setReportData(res.data.data || []);
        setWeekDays(res.data.weekInfo?.days || []);
        if (Array.isArray(res.data.departments) && res.data.departments.length > 0) {
          setDepartmentsList((prev) => Array.from(new Set([...prev, ...res.data.departments])));
        }
      } else {
        message.error(res.data?.message || 'Failed to load report');
      }
    } catch (err) {
      console.error('Error fetching labour wages report:', err);
      message.error(err.response?.data?.message || 'Failed to fetch labour wages report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [dateRange, department]);

  // Navigation Handlers
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/reports');
    }
  };

  const handleThisWeek = () => {
    setDateRange(getCurrentMonSat());
  };

  const handlePrev = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;
    const diffDays = dateRange[1].diff(dateRange[0], 'day') + 1;
    const newStart = dateRange[0].subtract(diffDays, 'day').startOf('day');
    const newEnd = dateRange[1].subtract(diffDays, 'day').endOf('day');
    setDateRange([newStart, newEnd]);
  };

  const handleNext = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;
    const diffDays = dateRange[1].diff(dateRange[0], 'day') + 1;
    const newStart = dateRange[0].add(diffDays, 'day').startOf('day');
    const newEnd = dateRange[1].add(diffDays, 'day').endOf('day');
    setDateRange([newStart, newEnd]);
  };

  const handleRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dayjs(dates[0]).startOf('day'), dayjs(dates[1]).endOf('day')]);
    }
  };

  // Download Excel
  const handleDownloadExcel = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;
    setDownloading(true);
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');

      const params = {
        startDate,
        endDate,
        format: 'excel',
      };
      if (department && department !== 'all') params.department = department;
      if (searchQuery) params.search = searchQuery;

      const response = await api.get('/admin/reports/labour-wages', {
        params,
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Labour_Wages_Report_${startDate}_to_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Labour wages report downloaded successfully');
    } catch (err) {
      console.error('Error downloading excel:', err);
      message.error('Failed to download excel report');
    } finally {
      setDownloading(false);
    }
  };

  // Dynamic calculated totals across all rendered workers
  const computedSummary = React.useMemo(() => {
    let totHours = 0;
    let activeCnt = 0;

    reportData.forEach(r => {
      totHours += r.totalWorkHours || 0;
      if (r.totalWorkHours > 0) activeCnt++;
    });

    return {
      totalWorkers: reportData.length,
      activeWorkers: activeCnt,
      totalHoursWorked: Number(totHours.toFixed(2)),
      avgHoursPerWorker: activeCnt > 0 ? Number((totHours / activeCnt).toFixed(2)) : 0,
    };
  }, [reportData]);

  // Combined unique departments from API and reportData
  const availableDepartments = React.useMemo(() => {
    const set = new Set(departmentsList);
    reportData.forEach((r) => {
      if (r.department && r.department.trim() && r.department !== '-') {
        set.add(r.department.trim());
      }
    });
    return Array.from(set).filter(Boolean).sort();
  }, [departmentsList, reportData]);

  // Table Columns
  const columns = [
    {
      title: 'S.N.',
      dataIndex: 'sn',
      key: 'sn',
      width: 55,
      align: 'center',
      fixed: 'left',
    },
    {
      title: 'Labour / Worker Details',
      key: 'workerInfo',
      width: 210,
      fixed: 'left',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 13 }}>
            {record.name}
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
            ID: <strong>{record.staffId}</strong>
            {record.phone && record.phone !== '-' && <span> | {record.phone}</span>}
          </div>
          <div style={{ marginTop: 3 }}>
            <Tag color="geekblue" style={{ fontSize: 10, padding: '0 5px', borderRadius: 4, margin: 0, marginRight: 4 }}>
              {record.department || 'General'}
            </Tag>
            <Tag color="cyan" style={{ fontSize: 10, padding: '0 5px', borderRadius: 4, margin: 0 }}>
              {record.designation || 'Worker'}
            </Tag>
          </div>
        </div>
      ),
    },
    // Dynamic columns for each day in range (Mon to Sat)
    ...weekDays.map((d) => ({
      title: (
        <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
          <div style={{ fontWeight: 700, color: d.isSunday ? '#ff4d4f' : '#1e293b' }}>{d.dayName}</div>
          <div style={{ fontSize: 10, color: '#64748b' }}>{d.shortDate}</div>
        </div>
      ),
      key: `day_${d.date}`,
      width: 100,
      align: 'center',
      render: (_, record) => {
        const dayInfo = record.days && record.days[d.date];
        const hrs = Number(dayInfo?.workHours || 0);

        if (hrs > 0) {
          const inOutText = dayInfo.inTime && dayInfo.outTime
            ? `${dayInfo.inTime} → ${dayInfo.outTime}`
            : dayInfo.inTime ? `In: ${dayInfo.inTime}` : 'Punch logged';

          return (
            <Tooltip title={`${d.fullDayName} (${d.shortDate}): ${hrs} hrs worked. ${inOutText}`}>
              <div>
                <Tag
                  color={hrs >= 8 ? 'green' : 'orange'}
                  style={{
                    fontWeight: 700,
                    fontSize: 12,
                    borderRadius: 6,
                    padding: '1px 6px',
                    margin: 0,
                    cursor: 'pointer',
                    border: 'none',
                  }}
                >
                  {hrs.toFixed(1)}h
                </Tag>
              </div>
            </Tooltip>
          );
        }

        if (dayInfo?.statusType === 'weekly_off' || dayInfo?.status === 'weekly_off') {
          return (
            <Tooltip title={`${d.fullDayName} (${d.shortDate}): Assigned Weekly Off`}>
              <span style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 600 }}>
                OFF
              </span>
            </Tooltip>
          );
        }

        if (dayInfo?.statusType === 'holiday' || dayInfo?.status === 'holiday') {
          return (
            <Tooltip title={`${d.fullDayName} (${d.shortDate}): Holiday ${dayInfo.holidayName ? `(${dayInfo.holidayName})` : ''}`}>
              <Tag color="purple" style={{ fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '0 4px', margin: 0 }}>
                HOLIDAY
              </Tag>
            </Tooltip>
          );
        }

        if (dayInfo?.statusType === 'leave' || dayInfo?.status === 'leave') {
          return (
            <Tooltip title={`${d.fullDayName} (${d.shortDate}): Approved Leave`}>
              <Tag color="blue" style={{ fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '0 4px', margin: 0 }}>
                LEAVE
              </Tag>
            </Tooltip>
          );
        }

        return (
          <span style={{ fontSize: 11, color: '#cf1322', fontWeight: 600 }}>
            0.0h
          </span>
        );
      },
    })),
    {
      title: (
        <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
          <div style={{ fontWeight: 700, color: '#0958d9' }}>Total Hours</div>
          <div style={{ fontSize: 10, color: '#64748b' }}>{weekDays.length} Days</div>
        </div>
      ),
      key: 'totalHours',
      width: 110,
      align: 'center',
      render: (_, record) => (
        <div>
          <Tag color="blue" style={{ fontSize: 13, fontWeight: 700, borderRadius: 8, padding: '2px 8px', margin: 0 }}>
            {record.totalWorkHours.toFixed(1)} hrs
          </Tag>
          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
            {record.daysWorked} / {weekDays.length} Days Worked
          </div>
        </div>
      ),
    },
  ];

  const reportContent = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            
            {/* 1. Header & Controls Card */}
            <Card
              style={{
                borderRadius: 14,
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
              }}
              bodyStyle={{ padding: '16px 20px' }}
            >
              <Row gutter={[16, 16]} align="middle" justify="space-between">
                
                {/* Left: Back Button, Week Navigation & Date Range Picker */}
                <Col xs={24} md={16}>
                  <Space wrap size="middle" align="center">
                    <Button
                      icon={<ArrowLeftOutlined />}
                      onClick={handleBack}
                      style={{
                        fontWeight: 600,
                        borderRadius: 6,
                        color: '#1e293b',
                      }}
                    >
                      Back
                    </Button>

                    <Button.Group>
                      <Button icon={<LeftOutlined />} onClick={handlePrev} title="Previous Week">
                        Prev
                      </Button>
                      <Button onClick={handleThisWeek} type={dateRange[0]?.isSame(getCurrentMonSat()[0], 'day') ? 'primary' : 'default'}>
                        This Week (Mon-Sat)
                      </Button>
                      <Button icon={<RightOutlined />} onClick={handleNext} title="Next Week">
                        Next
                      </Button>
                    </Button.Group>

                    <RangePicker
                      value={dateRange && dateRange[0] && dateRange[1] ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
                      onChange={handleRangeChange}
                      format="DD MMM YYYY"
                      allowClear={false}
                      style={{ borderRadius: 6, fontWeight: 500, width: 240 }}
                    />

                    <Tag color="blue" style={{ fontSize: 13, padding: '3px 8px', borderRadius: 6, fontWeight: 600 }}>
                      {dateRange[0]?.format('DD MMM YYYY')} – {dateRange[1]?.format('DD MMM YYYY')}
                    </Tag>
                  </Space>
                </Col>

                {/* Right: Actions (Refresh & Download Excel) */}
                <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                  <Space wrap size="small">
                    <Button icon={<ReloadOutlined />} onClick={fetchReport} loading={loading}>
                      Refresh
                    </Button>
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      loading={downloading}
                      onClick={handleDownloadExcel}
                      style={{ background: '#1677ff', borderColor: '#1677ff', fontWeight: 600 }}
                    >
                      Download Excel (.xlsx)
                    </Button>
                  </Space>
                </Col>
              </Row>

              <Divider style={{ margin: '14px 0' }} />

              {/* Secondary Filter Row: Department, Search, Wage Calculator */}
              <Row gutter={[12, 12]} align="middle">

                <Col xs={24} sm={10} md={8}>
                  <Select
                    value={department}
                    onChange={setDepartment}
                    style={{ width: '100%' }}
                    placeholder="Filter Department"
                    showSearch
                    optionFilterProp="children"
                  >
                    <Option value="all">All Departments</Option>
                    {availableDepartments.map(d => (
                      <Option key={d} value={d}>{d}</Option>
                    ))}
                  </Select>
                </Col>

                <Col xs={24} sm={14} md={16}>
                  <Input
                    placeholder="Search Worker Name / ID..."
                    prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onPressEnter={fetchReport}
                    allowClear
                  />
                </Col>
              </Row>
            </Card>

            {/* 2. Executive Metric Cards */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12}>
                <Card bodyStyle={{ padding: '16px 20px' }} style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>TOTAL WORKERS</span>
                    <TeamOutlined style={{ color: '#1677ff', fontSize: 18 }} />
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', marginTop: 4 }}>
                    {computedSummary.totalWorkers}
                  </div>
                  <div style={{ fontSize: 11, color: '#52c41a', marginTop: 2 }}>
                    ✓ {computedSummary.activeWorkers} Workers Active in this period
                  </div>
                </Card>
              </Col>

              <Col xs={24} sm={12} md={12}>
                <Card bodyStyle={{ padding: '16px 20px' }} style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>TOTAL WORKING HOURS</span>
                    <ClockCircleOutlined style={{ color: '#0958d9', fontSize: 18 }} />
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#0958d9', marginTop: 4 }}>
                    {computedSummary.totalHoursWorked.toLocaleString('en-IN')} <span style={{ fontSize: 14 }}>hrs</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    Avg: {computedSummary.avgHoursPerWorker} hrs / worker
                  </div>
                </Card>
              </Col>
            </Row>

            {/* 3. Timesheet Table */}
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 4, height: 20, background: '#1677ff', borderRadius: 2 }} />
                  <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                    Labour / Worker Actual Working Hours Register
                  </span>
                  <Tag color="blue" style={{ marginLeft: 6, fontWeight: 600 }}>
                    {dateRange[0]?.format('DD MMM YYYY')} — {dateRange[1]?.format('DD MMM YYYY')} ({weekDays.length} Days)
                  </Tag>
                </div>
              }
              style={{
                borderRadius: 14,
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
              }}
              bodyStyle={{ padding: '12px 16px' }}
            >
              <Table
                columns={columns}
                dataSource={reportData}
                rowKey="userId"
                loading={loading}
                scroll={{ x: 1200 }}
                pagination={{
                  defaultPageSize: 100,
                  showSizeChanger: true,
                  pageSizeOptions: ['50', '100', '200'],
                  showTotal: (total) => `Total ${total} Workers Listed`,
                }}
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ background: '#f8fafc', fontWeight: 700 }}>
                      <Table.Summary.Cell index={0} align="center" colSpan={2}>
                        TOTAL SUMMARY
                      </Table.Summary.Cell>
                      {weekDays.map((d, i) => {
                        const daySum = reportData.reduce((acc, r) => acc + (r.days[d.date]?.workHours || 0), 0);
                        return (
                          <Table.Summary.Cell key={i} index={2 + i} align="center">
                            <span style={{ color: '#0958d9' }}>{daySum.toFixed(1)}h</span>
                          </Table.Summary.Cell>
                        );
                      })}
                      <Table.Summary.Cell index={2 + weekDays.length} align="center">
                        <Tag color="blue" style={{ fontSize: 13, fontWeight: 700 }}>
                          {computedSummary.totalHoursWorked} hrs
                        </Tag>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </Card>

          </Space>
  );

  return isEmbedded ? (
    <div style={{ padding: 16 }}>
      {reportContent}
    </div>
  ) : (
    <Layout style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <Sidebar collapsed={collapsed} />
      <Layout style={{ marginLeft: collapsed ? 80 : 250, transition: 'all 0.2s', background: '#f8fafc' }}>
        <MainHeader collapsed={collapsed} setCollapsed={setCollapsed} title="Labour / Worker Wages Report" />
        <Content style={{ margin: '24px 24px', padding: 0, minHeight: 280 }}>
          {reportContent}
        </Content>
      </Layout>
    </Layout>
  );
}
